import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
// src/combineKeys.ts
import { Observable } from "rxjs";

var combineKeys = function combineKeys(keys$, getInner$) {
  return new Observable(function (observer) {
    var innerSubscriptions = /* @__PURE__ */new Map();
    var changes = /* @__PURE__ */new Set();
    var currentValue = /* @__PURE__ */new Map();
    var updatingSource = false;
    var isPristine = true;

    var next = function next() {
      if (!updatingSource) {
        var result = Object.assign(new Map(currentValue), {
          changes: changes
        });
        changes = /* @__PURE__ */new Set();
        isPristine = false;
        observer.next(result);
      }
    };

    var subscription = keys$.subscribe(function (nextKeysArr) {
      updatingSource = true;
      var nextKeys = new Set(nextKeysArr);
      innerSubscriptions.forEach(function (sub, key) {
        if (!nextKeys.has(key)) {
          sub.unsubscribe();
          innerSubscriptions.delete(key);

          if (currentValue.has(key)) {
            changes.add(key);
            currentValue.delete(key);
          }
        } else {
          nextKeys.delete(key);
        }
      });
      nextKeys.forEach(function (key) {
        innerSubscriptions.set(key, getInner$(key).subscribe(function (x) {
          if (!currentValue.has(key) || currentValue.get(key) !== x) {
            changes.add(key);
            currentValue.set(key, x);
            next();
          }
        }, function (e) {
          observer.error(e);
        }));
      });
      updatingSource = false;
      if (changes.size || isPristine && !nextKeys.size) next();
    }, function (e) {
      observer.error(e);
    }, function () {
      observer.complete();
    });
    return function () {
      subscription.unsubscribe();
      innerSubscriptions.forEach(function (sub) {
        sub.unsubscribe();
      });
    };
  });
}; // src/createSignal.ts


import { identity, Subject } from "rxjs";

function createSignal() {
  var mapper = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : identity;
  var subject = new Subject();
  return [subject.asObservable(), function () {
    return subject.next(mapper.apply(void 0, arguments));
  }];
} // src/createKeyedSignal.ts


import { Observable as Observable3 } from "rxjs";

function createKeyedSignal(keySelector, mapper) {
  var observersMap = /* @__PURE__ */new Map();
  return [function (key) {
    var res = new Observable3(function (observer) {
      if (!observersMap.has(key)) {
        observersMap.set(key, /* @__PURE__ */new Set());
      }

      var set = observersMap.get(key);
      set.add(observer);
      return function () {
        set.delete(observer);

        if (set.size === 0) {
          observersMap.delete(key);
        }
      };
    });
    res.key = key;
    return res;
  }, function () {
    var _a;

    var payload = mapper ? mapper.apply(void 0, arguments) : arguments.length === 2 ? arguments.length <= 1 ? undefined : arguments[1] : arguments.length <= 0 ? undefined : arguments[0];
    var key = keySelector ? keySelector(payload) : arguments.length <= 0 ? undefined : arguments[0];
    (_a = observersMap.get(key)) == null ? void 0 : _a.forEach(function (o) {
      o.next(payload);
    });
  }];
} // src/mergeWithKey.ts


import { merge, from } from "rxjs";
import { map } from "rxjs/operators";

var mergeWithKey = function mergeWithKey(input) {
  for (var _len = arguments.length, optionalArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    optionalArgs[_key - 1] = arguments[_key];
  }

  return merge.apply(void 0, _toConsumableArray(Object.entries(input).map(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        type = _ref2[0],
        stream = _ref2[1];

    return from(stream).pipe(map(function (payload) {
      return {
        type: type,
        payload: payload
      };
    }));
  }).concat(optionalArgs)));
}; // src/partitionByKey.ts


import { shareLatest } from "@react-rxjs/core";
import { noop, Observable as Observable5, Subject as Subject2, Subscription as Subscription2, identity as identity2 } from "rxjs";
import { map as map2 } from "rxjs/operators";

function partitionByKey(stream, keySelector, streamSelector) {
  var groupedObservables$ = new Observable5(function (subscriber) {
    var groups = /* @__PURE__ */new Map();
    var emitted = false;
    var sourceCompleted = false;
    var sub = stream.subscribe(function (x) {
      var key = keySelector(x);

      if (groups.has(key)) {
        return groups.get(key).source.next(x);
      }

      var subject = new Subject2();
      var res = shareLatest()((streamSelector || identity2)(subject, key));
      res.key = key;
      var innerGroup = {
        source: subject,
        observable: res,
        subscription: new Subscription2()
      };
      groups.set(key, innerGroup);
      innerGroup.subscription = res.subscribe(noop, function (e) {
        return subscriber.error(e);
      }, function () {
        groups.delete(key);
        subscriber.next(mapGroups(groups));

        if (groups.size === 0 && sourceCompleted) {
          subscriber.complete();
        }
      });
      subject.next(x);
      subscriber.next(mapGroups(groups));
      emitted = true;
    }, function (e) {
      sourceCompleted = true;

      if (groups.size) {
        groups.forEach(function (g) {
          return g.source.error(e);
        });
      } else {
        subscriber.error(e);
      }
    }, function () {
      sourceCompleted = true;

      if (groups.size) {
        groups.forEach(function (g) {
          return g.source.complete();
        });
      } else {
        subscriber.complete();
      }
    });
    if (!emitted) subscriber.next(mapGroups(groups));
    return function () {
      sub.unsubscribe();
      groups.forEach(function (g) {
        g.source.unsubscribe();
        g.subscription.unsubscribe();
      });
    };
  }).pipe(shareLatest());
  return [function (key) {
    return getGroupedObservable(groupedObservables$, key);
  }, groupedObservables$.pipe(map2(function (x) {
    return Array.from(x.keys());
  }))];
}

function mapGroups(groups) {
  return new Map(Array.from(groups.entries()).map(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
        key = _ref4[0],
        group = _ref4[1];

    return [key, group.observable];
  }));
}

var getGroupedObservable = function getGroupedObservable(source$, key) {
  var result = new Observable5(function (observer) {
    var innerSub;
    var outterSub = source$.subscribe(function (n) {
      var _a;

      innerSub = innerSub || ((_a = n.get(key)) == null ? void 0 : _a.subscribe(observer));
    }, function (e) {
      observer.error(e);
    }, function () {
      observer.complete();
    });
    return function () {
      innerSub == null ? void 0 : innerSub.unsubscribe();
      outterSub.unsubscribe();
    };
  });
  result.key = key;
  return result;
}; // src/suspend.ts


import { from as from2 } from "rxjs";
import { SUSPENSE } from "@react-rxjs/core"; // src/internal-utils.ts

import { Observable as Observable6 } from "rxjs";

var defaultStart = function defaultStart(value) {
  return function (source$) {
    return new Observable6(function (observer) {
      var emitted = false;
      var subscription = source$.subscribe(function (x) {
        emitted = true;
        observer.next(x);
      }, function (e) {
        observer.error(e);
      }, function () {
        observer.complete();
      });

      if (!emitted) {
        observer.next(value);
      }

      return subscription;
    });
  };
}; // src/suspend.ts


var suspend = function suspend(source$) {
  return defaultStart(SUSPENSE)(from2(source$));
}; // src/suspended.ts


var suspended = function suspended() {
  return suspend;
}; // src/switchMapSuspended.ts


import { pipe } from "rxjs";
import { switchMap } from "rxjs/operators";

var switchMapSuspended = function switchMapSuspended(project) {
  return pipe(switchMap(function (x, index) {
    return suspend(project(x, index));
  }));
}; // src/selfDependant.ts


import { Subject as Subject3 } from "rxjs";
import { tap } from "rxjs/operators";

var selfDependant = function selfDependant() {
  var mirrored$ = new Subject3();
  return [mirrored$.asObservable(), function () {
    return tap(mirrored$);
  }];
}; // src/contextBinder.ts


import { bind } from "@react-rxjs/core";

var execSelf = function execSelf(fn) {
  return fn();
};

function contextBinder() {
  for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    args[_key2] = arguments[_key2];
  }

  var useArgs = function useArgs() {
    return args.map(execSelf);
  };

  return function () {
    var _bind$apply = bind.apply(null, arguments),
        _bind$apply2 = _slicedToArray(_bind$apply, 2),
        hook = _bind$apply2[0],
        getter = _bind$apply2[1];

    return [function () {
      for (var _len3 = arguments.length, args2 = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        args2[_key3] = arguments[_key3];
      }

      return hook.apply(void 0, _toConsumableArray(useArgs()).concat(args2));
    }, getter];
  };
} // src/createListener.ts


function createListener() {
  return createSignal.apply(void 0, arguments);
}

export { combineKeys, contextBinder, createKeyedSignal, createListener, createSignal, mergeWithKey, partitionByKey, selfDependant, suspend, suspended, switchMapSuspended };